import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { AppButton } from '@/shared/components/buttons/AppButton';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { BACKDOOR_TOKEN_KEY } from '@/shared/constants/auth';

/**
 * Extract the redirect path from the raw URL string.
 *
 * We cannot rely on URLSearchParams for this because the parent app may
 * build the URL with an unencoded `?` inside the redirect value:
 *   &redirect=/notifications/relay?notifId=xxx
 *
 * URLSearchParams handles it fine when parsing, but App.tsx's
 * `URL.toString()` after `searchParams.delete('token')` re-encodes
 * everything and corrupts the value. And `useSearchParams` can be
 * out-of-sync with the actual URL after a `history.replaceState`.
 *
 * Parsing from the raw string avoids all of these pitfalls.
 */
function extractRedirectFromRawUrl(): string {
  const raw = window.location.search; // e.g. ?token=JWT&redirect=/notifications/relay?notifId=xxx
  const marker = 'redirect=';
  const idx = raw.indexOf('&' + marker);
  if (idx !== -1) {
    return decodeURIComponent(raw.substring(idx + 1 + marker.length));
  }
  // redirect might be the first param (unlikely but defensive)
  const idx2 = raw.indexOf('?' + marker);
  if (idx2 !== -1) {
    const afterMarker = raw.substring(idx2 + 1 + marker.length);
    // Stop at the next & that is clearly a top-level separator (before any =)
    const ampIdx = afterMarker.indexOf('&');
    if (ampIdx !== -1 && afterMarker.indexOf('?') > ampIdx) {
      return decodeURIComponent(afterMarker.substring(0, ampIdx));
    }
    return decodeURIComponent(afterMarker);
  }
  return '/';
}

/**
 * TokenHandler Component
 *
 * Handles deep-linking authentication flow from parent mobile apps:
 * 1. User clicks link in parent app with ?token=xxx&redirect=/path
 * 2. Browser/PWA opens with this component
 * 3. Token is extracted and stored
 * 4. User is redirected to the target page
 */
export function TokenHandler() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [message, setMessage] = useState('Authentification en cours...');

  useEffect(() => {
    // Read token from the raw URL — immune to replaceState side effects
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('token');
    const redirectPath = extractRedirectFromRawUrl();

    if (!token) {
      // No token in URL — check if user is already authenticated
      const existingToken =
        sessionStorage.getItem(BACKDOOR_TOKEN_KEY) || localStorage.getItem(BACKDOOR_TOKEN_KEY);

      if (existingToken && redirectPath !== '/') {
        // User already authenticated: honour the redirect (e.g. notification deep link)
        navigate(redirectPath, { replace: true });
        return;
      }

      setStatus('error');
      setMessage("Aucun token d'authentification trouvé dans l'URL");
      return;
    }

    // Store token in both storages for PWA persistence
    sessionStorage.setItem(BACKDOOR_TOKEN_KEY, token);
    localStorage.setItem(BACKDOOR_TOKEN_KEY, token);

    // Navigate to requested page
    navigate(redirectPath, { replace: true });
  }, [navigate]);

  const handleManualRedirect = () => {
    navigate(extractRedirectFromRawUrl(), { replace: true });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {status === 'processing' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Veuillez patienter...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="error.main">
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Impossible de traiter le lien d'authentification.
            </Typography>
            <AppButton variant="primary" onClick={handleManualRedirect} fullWidth>
              Continuer vers l'application
            </AppButton>
          </>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
        ROCKEASE Bourse aux Matériaux
      </Typography>
    </Box>
  );
}
